import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  TeamService,
  LeagueService,
  SeasonService,
  LeagueSeasonService,
  UserService,
  PredictionService,
  ContestantService
} from '../../services/firebaseService';
import {
  TrophyIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';
import { getUserClaimedTeams } from '../../utils/teamUtils';
import { isWeekOpenForPredictions } from '../../utils/predictionUtils';
import {
  normalizeToContestantName,
  getContestantNamesFromIds,
  normalizeVotedOutToOrderedContestantIds
} from '../../utils/contestantUtils';
import toast from 'react-hot-toast';

const DRAFT_LINK_STATUSES = [
  'notStarted',
  'inProgress',
  'complete',
  'finalized',
];

function getDraftHomeLinkLabel(draftStatus) {
  switch (draftStatus) {
    case 'inProgress':
      return 'Go to Draft';
    case 'finalized':
      return 'View draft results';
    default:
      return 'View Draft';
  }
}

function isDraftHomeLinkProminent(draftStatus) {
  return draftStatus !== 'finalized';
}

/**
 * Homepage link to the player draft board. Prominent during live draft;
 * subtle secondary styling once finalized (render with placement="end").
 */
function DraftHomeLink({
  leagueId,
  seasonId,
  draftStatus,
  placement = 'start',
  className = '',
}) {
  if (!DRAFT_LINK_STATUSES.includes(draftStatus)) return null;

  const prominent = isDraftHomeLinkProminent(draftStatus);
  if (prominent && placement !== 'start') return null;
  if (!prominent && placement !== 'end') return null;

  const linkClassName = [
    prominent
      ? 'px-6 py-3 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 font-medium min-h-[44px] inline-flex items-center'
      : 'px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium min-h-[44px] inline-flex items-center',
    placement === 'end' ? 'ml-auto' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Link
      to={`/player/draft/${leagueId}/${seasonId}`}
      className={linkClassName}
    >
      {getDraftHomeLinkLabel(draftStatus)}
    </Link>
  );
}

/**
 * Collapsible section showing past (closed) league-seasons with standings link and team links.
 */
function PastSeasonsSection({ closedTeamsData, isOpen, onToggle }) {
  if (!closedTeamsData || closedTeamsData.length === 0) return null;

  const byLeagueSeason = {};
  closedTeamsData.forEach((t) => {
    const key = `${t.league.id}:${t.season.id}`;
    if (!byLeagueSeason[key]) {
      byLeagueSeason[key] = { league: t.league, season: t.season, teams: [] };
    }
    byLeagueSeason[key].teams.push(t);
  });
  const groups = Object.values(byLeagueSeason);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 border-t border-gray-200">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full text-left text-gray-600 hover:text-gray-900 font-medium"
      >
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 shrink-0" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 shrink-0" />
        )}
        <ArchiveBoxIcon className="h-5 w-5 shrink-0" />
        <span>Inactive Teams ({groups.length})</span>
      </button>
      {isOpen && (
        <div className="mt-4 space-y-6">
          {groups.map((group) => {
            const { league, season, teams } = group;
            return (
              <div
                key={`${league.id}:${season.id}`}
                className="bg-white rounded-lg shadow border border-gray-200 p-4"
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {league.name}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {season.name || season.id}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <Link
                    to={`/player/standings/${league.id}/${season.id}`}
                    className="text-survivor-600 hover:text-survivor-700 font-medium"
                  >
                    View standings
                  </Link>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-600">Your teams:</span>
                  {teams.map((teamData, idx) => (
                    <span key={teamData.team.id}>
                      {idx > 0 && <span className="text-gray-400 mr-1">, </span>}
                      <Link
                        to={`/player/teams/${teamData.team.id}`}
                        className="text-survivor-600 hover:text-survivor-700 font-medium"
                      >
                        {teamData.team.name}
                      </Link>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PlayerHomepage() {
  const { currentUser, userProfile, loading: authLoading, updateUserProfile } = useAuth();
  
  const [teamsData, setTeamsData] = useState([]); // Array of { team, league, season, leagueSeason, rank } - active only
  const [closedTeamsData, setClosedTeamsData] = useState([]); // Same shape - closed league-seasons only
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [state, setState] = useState(null); // 'no-teams', 'one-team', 'multiple-one-league', 'multiple-leagues'
  const [unclaimingTeamId, setUnclaimingTeamId] = useState(null);
  const [pastSeasonsOpen, setPastSeasonsOpen] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!currentUser || !userProfile) {
        // Not logged in - this shouldn't happen as HomeRoute handles it
        // But handle gracefully just in case
        setState('no-teams');
        setLoading(false);
        return;
      }
      loadUserTeams();
    }
  }, [currentUser, userProfile, authLoading]);

  // Load selected team from localStorage on mount
  useEffect(() => {
    if (teamsData.length > 0 && !selectedTeamId) {
      const savedTeamId = localStorage.getItem('selectedTeamId');
      if (savedTeamId && teamsData.find(t => t.team.id === savedTeamId)) {
        setSelectedTeamId(savedTeamId);
      } else {
        setSelectedTeamId(teamsData[0].team.id);
      }
    }
  }, [teamsData, selectedTeamId]);

  // Save selected team to localStorage
  useEffect(() => {
    if (selectedTeamId) {
      localStorage.setItem('selectedTeamId', selectedTeamId);
    }
  }, [selectedTeamId]);

  const loadUserTeams = async () => {
    try {
      setLoading(true);
      setError('');

      if (!userProfile || !userProfile.teams) {
        setState('no-teams');
        setLoading(false);
        return;
      }

      // Get user's claimed teams
      const claimedTeams = getUserClaimedTeams(userProfile.teams);
      
      if (claimedTeams.length === 0) {
        setState('no-teams');
        setLoading(false);
        return;
      }

      // Load full data for each team
      const teamsDataPromises = claimedTeams.map(async (claimedTeam) => {
        try {
          // Load team, league, season, and league-season data in parallel
          const [team, league, season, leagueSeason] = await Promise.all([
            TeamService.getTeam(claimedTeam.leagueId, claimedTeam.seasonId, claimedTeam.teamId),
            LeagueService.getLeague(claimedTeam.leagueId),
            SeasonService.getSeason(claimedTeam.seasonId),
            LeagueSeasonService.getLeagueSeason(claimedTeam.leagueId, claimedTeam.seasonId)
          ]);

          if (!team || !league || !season) {
            return null;
          }

          // Calculate rank by getting all teams in league-season (using utility for tie handling)
          const allTeams = await TeamService.getTeamsForLeagueSeason(
            claimedTeam.leagueId,
            claimedTeam.seasonId
          );
          const { calculateRanks } = await import('../../utils/rankingUtils');
          const rankedTeams = calculateRanks(allTeams, (t) => t.currentScore || 0);
          const rankedTeam = rankedTeams.find(t => t.id === team.id);
          const rank = rankedTeam ? rankedTeam.rank : allTeams.length;

          // Find current open week
          const openWeeks = season.weeks?.filter(w => isWeekOpenForPredictions(w)) || [];
          const currentWeek = openWeeks.length > 0 ? openWeeks[0] : null;

          // Load prediction for current week if it exists
          let currentPrediction = null;
          if (currentWeek) {
            try {
              currentPrediction = await PredictionService.getTeamPrediction(
                claimedTeam.leagueId,
                claimedTeam.seasonId,
                claimedTeam.teamId,
                currentWeek.weekNumber
              );
            } catch (err) {
              console.error(`Error loading prediction for team ${claimedTeam.teamId}:`, err);
            }
          }

          // Load contestants for this season
          let contestants = [];
          try {
            contestants = await ContestantService.getContestantsBySeason(claimedTeam.seasonId);
          } catch (err) {
            console.error(`Error loading contestants for season ${claimedTeam.seasonId}:`, err);
          }

          return {
            team,
            league,
            season,
            leagueSeason,
            rank,
            currentWeek,
            currentPrediction,
            contestants,
            claimedAt: claimedTeam.claimedAt
          };
        } catch (err) {
          console.error(`Error loading team ${claimedTeam.teamId}:`, err);
          return null;
        }
      });

      const loadedTeams = (await Promise.all(teamsDataPromises)).filter(Boolean);

      // Split into active (league-season isActive) and closed
      const activeTeams = loadedTeams.filter(t => t.leagueSeason?.isActive === true);
      const closedTeams = loadedTeams.filter(t => t.leagueSeason?.isActive !== true);

      setTeamsData(activeTeams);
      setClosedTeamsData(closedTeams);

      // Main UI state is based only on active teams
      if (activeTeams.length === 0) {
        setState('no-teams');
        setLoading(false);
        return;
      }

      if (activeTeams.length === 1) {
        setState('one-team');
        setSelectedTeamId(activeTeams[0].team.id);
      } else {
        const uniqueLeagues = new Set(activeTeams.map(t => t.league.id));
        if (uniqueLeagues.size === 1) {
          setState('multiple-one-league');
        } else {
          setState('multiple-leagues');
        }
      }

    } catch (err) {
      console.error('Error loading user teams:', err);
      setError(err.message || 'Failed to load your teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTeamScore = (team) => {
    return team.currentScore || 0;
  };

  const getPredictionDisplay = (prediction, contestants) => {
    if (!prediction || !prediction.predictions) {
      return null;
    }

    const parts = [];
    const preds = prediction.predictions;

    // Voted out: string ID, ID array, or double/triple slots { contestantId, position }
    if (preds.votedOut) {
      const votedOutIds = normalizeVotedOutToOrderedContestantIds(preds.votedOut);
      const names = getContestantNamesFromIds(votedOutIds, contestants);
      if (names.length > 0) {
        parts.push(`Voted Out: ${names.join(', ')}`);
      }
    }

    // Winner
    if (preds.winner) {
      const name = normalizeToContestantName(preds.winner, contestants);
      if (name !== 'N/A') {
        parts.push(`Winner: ${name}`);
      }
    }

    // Other predictions (if needed)
    if (preds.fireMakingLoser) {
      const name = normalizeToContestantName(preds.fireMakingLoser, contestants);
      if (name !== 'N/A') {
        parts.push(`Fire Making Loser: ${name}`);
      }
    }

    if (preds.immunityWinner) {
      const name = normalizeToContestantName(preds.immunityWinner, contestants);
      if (name !== 'N/A') {
        parts.push(`Immunity: ${name}`);
      }
    }

    if (preds.rewardWinner) {
      const name = normalizeToContestantName(preds.rewardWinner, contestants);
      if (name !== 'N/A') {
        parts.push(`Reward: ${name}`);
      }
    }

    return parts.length > 0 ? parts.join(' • ') : null;
  };

  const getSelectedTeamData = () => {
    if (!selectedTeamId) return null;
    return teamsData.find(t => t.team.id === selectedTeamId);
  };

  const handleRetry = () => {
    loadUserTeams();
  };

  const handleUnclaimTeam = async (teamId, leagueId, seasonId) => {
    if (!currentUser) return;
    
    if (!window.confirm('Are you sure you want to unclaim this team? You will no longer be able to submit predictions for this team.')) {
      return;
    }

    try {
      setUnclaimingTeamId(teamId);
      
      await UserService.unclaimTeam(currentUser.uid, teamId, leagueId, seasonId);
      
      // Refresh user profile
      await updateUserProfile({});
      
      // Reload teams
      await loadUserTeams();
      
      toast.success('Team unclaimed successfully');
    } catch (error) {
      console.error('Error unclaiming team:', error);
      toast.error(error.message || 'Failed to unclaim team. Please try again.');
    } finally {
      setUnclaimingTeamId(null);
    }
  };

  // Loading state
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your teams...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Teams</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // State 1: No Teams (no active teams; may have closed teams)
  if (state === 'no-teams') {
    const hasPastSeasons = closedTeamsData.length > 0;
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div
          className={`px-4 flex flex-col ${hasPastSeasons ? 'py-16 pt-20 shrink-0' : 'flex-1 items-center justify-center py-8'}`}
        >
          <div
            className={`text-center max-w-md mx-auto ${hasPastSeasons ? 'pt-8 pb-12' : ''}`}
          >
            <TrophyIcon
              className={`text-gray-400 mx-auto ${hasPastSeasons ? 'h-10 w-10 mb-2' : 'h-16 w-16 mb-4'}`}
            />
            <h2
              className={`font-bold text-gray-900 ${hasPastSeasons ? 'text-xl mb-1' : 'text-2xl mb-2'}`}
            >
              Welcome to Survivor Fantasy League!
            </h2>
            <p className={`text-gray-600 ${hasPastSeasons ? 'mb-1 text-sm' : 'mb-4'}`}>
              {hasPastSeasons
                ? "You don't have any active teams. To get started, click the prediction link sent by your league manager."
                : "You haven't claimed any teams yet. To get started, click the prediction link sent by your league manager."}
            </p>
            <p className={`text-gray-500 ${hasPastSeasons ? 'text-xs' : 'text-sm'}`}>
              Once you claim a team, you'll be able to submit predictions and track your progress
              here.
            </p>
          </div>
        </div>
        <PastSeasonsSection
          closedTeamsData={closedTeamsData}
          isOpen={pastSeasonsOpen}
          onToggle={() => setPastSeasonsOpen(!pastSeasonsOpen)}
        />
      </div>
    );
  }

  // State 2: One Team
  if (state === 'one-team') {
    const teamData = teamsData[0];
    const { team, league, season, leagueSeason, rank, currentWeek, currentPrediction, contestants } = teamData;

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {leagueSeason?.draftStatus === 'inProgress' && (
            <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-amber-900 font-medium">
                Draft is in progress for {league.name}.
              </p>
              <Link
                to={`/player/draft/${league.id}/${season.id}`}
                className="inline-flex justify-center items-center min-h-[44px] px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
              >
                Go to draft
              </Link>
            </div>
          )}
          {/* Team Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 relative pb-16">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{team.name}</h2>
                <p className="text-gray-600 mt-1">{league.name}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Rank</div>
                <div className="text-3xl font-bold text-survivor-600">#{rank}</div>
              </div>
            </div>
            
            {/* Unclaim link in bottom right corner */}
            <div className="absolute bottom-4 right-6">
              <button
                onClick={() => handleUnclaimTeam(team.id, league.id, season.id)}
                disabled={unclaimingTeamId === team.id}
                className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {unclaimingTeamId === team.id ? 'Unclaiming...' : 'Unclaim team'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Total Score</div>
                <div className="text-2xl font-bold text-gray-900">{getTeamScore(team)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Current Week</div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeek ? (
                    <span className="flex items-center">
                      <ClockIcon className="h-5 w-5 text-green-500 mr-1" />
                      Week {currentWeek.weekNumber}
                    </span>
                  ) : (
                    <span className="text-gray-400">Closed</span>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Week Status</div>
                <div className="text-lg font-semibold">
                  {currentWeek ? (
                    <div>
                      <span className="text-green-600 flex items-center mb-1">
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Open for Predictions
                      </span>
                      {currentPrediction ? (
                        <div className="mt-2 text-sm font-normal text-gray-700">
                          <div className="text-xs text-gray-500 mb-1">Your Prediction:</div>
                          <div className="text-gray-900">
                            {getPredictionDisplay(currentPrediction, contestants) || 'Prediction submitted'}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm font-normal text-gray-500">
                          No prediction submitted yet
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500 flex items-center">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      No Open Weeks
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-3 items-center w-full">
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="start"
              />
              {currentWeek && (
                <Link
                  to={`/predictions2/${league.id}/${season.id}?teamId=${team.id}&week=${currentWeek.weekNumber}`}
                  className="px-6 py-3 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 font-medium"
                >
                  {currentPrediction ? 'Update Prediction' : 'Submit Prediction'}
                </Link>
              )}
              <Link
                to={`/player/teams/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Team Detail
              </Link>
              <Link
                to={`/player/standings/${league.id}/${season.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Standings
              </Link>
              <Link
                to={`/player/history/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View History
              </Link>
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="end"
              />
            </div>
          </div>
          <PastSeasonsSection
            closedTeamsData={closedTeamsData}
            isOpen={pastSeasonsOpen}
            onToggle={() => setPastSeasonsOpen(!pastSeasonsOpen)}
          />
        </div>
      </div>
    );
  }

  // State 3: Multiple Teams in One League
  if (state === 'multiple-one-league') {
    const selectedTeamData = getSelectedTeamData();
    const league = teamsData[0].league;
    const season = teamsData[0].season;
    const leagueSeason = selectedTeamData?.leagueSeason || teamsData[0].leagueSeason;

    if (!selectedTeamData) {
      return null; // Shouldn't happen, but handle gracefully
    }

    const { team, rank, currentWeek, currentPrediction, contestants } = selectedTeamData;

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Teams</h1>
            <p className="text-gray-600">{league.name}</p>
          </div>

          {/* Team Switcher */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Team
            </label>
            <select
              value={selectedTeamId || ''}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md focus:ring-survivor-500 focus:border-survivor-500"
            >
              {teamsData.map((teamData) => (
                <option key={teamData.team.id} value={teamData.team.id}>
                  {teamData.team.name} (Rank #{teamData.rank})
                </option>
              ))}
            </select>
          </div>

          {/* Selected Team Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 relative pb-16">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{team.name}</h2>
                <p className="text-gray-600 mt-1">{league.name}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Rank</div>
                <div className="text-3xl font-bold text-survivor-600">#{rank}</div>
              </div>
            </div>
            
            {/* Unclaim link in bottom right corner */}
            <div className="absolute bottom-4 right-6">
              <button
                onClick={() => handleUnclaimTeam(team.id, league.id, season.id)}
                disabled={unclaimingTeamId === team.id}
                className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {unclaimingTeamId === team.id ? 'Unclaiming...' : 'Unclaim team'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Total Score</div>
                <div className="text-2xl font-bold text-gray-900">{getTeamScore(team)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Current Week</div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeek ? `Week ${currentWeek.weekNumber}` : 'Closed'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Week Status</div>
                <div className="text-lg font-semibold">
                  {currentWeek ? (
                    <div>
                      <span className="text-green-600 flex items-center mb-1">
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Open for Predictions
                      </span>
                      {currentPrediction ? (
                        <div className="mt-2 text-sm font-normal text-gray-700">
                          <div className="text-xs text-gray-500 mb-1">Your Prediction:</div>
                          <div className="text-gray-900">
                            {getPredictionDisplay(currentPrediction, contestants) || 'Prediction submitted'}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm font-normal text-gray-500">
                          No prediction submitted yet
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500 flex items-center">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      No Open Weeks
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-3 items-center w-full">
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="start"
              />
              {currentWeek && (
                <Link
                  to={`/predictions2/${league.id}/${season.id}?teamId=${team.id}&week=${currentWeek.weekNumber}`}
                  className="px-6 py-3 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 font-medium"
                >
                  {currentPrediction ? 'Update Prediction' : 'Submit Prediction'}
                </Link>
              )}
              <Link
                to={`/player/teams/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Team Detail
              </Link>
              <Link
                to={`/player/standings/${league.id}/${season.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Standings
              </Link>
              <Link
                to={`/player/history/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View History
              </Link>
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="end"
              />
            </div>
          </div>

          {/* All Teams Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">All Your Teams</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamsData.map((teamData) => (
                <div
                  key={teamData.team.id}
                  onClick={() => setSelectedTeamId(teamData.team.id)}
                  className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    selectedTeamId === teamData.team.id
                      ? 'border-survivor-600 bg-survivor-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {teamData.team.name}
                    </h4>
                    <span className="text-sm font-medium text-survivor-600">#{teamData.rank}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Score: {getTeamScore(teamData.team)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <PastSeasonsSection
            closedTeamsData={closedTeamsData}
            isOpen={pastSeasonsOpen}
            onToggle={() => setPastSeasonsOpen(!pastSeasonsOpen)}
          />
        </div>
      </div>
    );
  }

  // State 4: Teams in Different Leagues
  if (state === 'multiple-leagues') {
    // Group teams by league
    const teamsByLeague = {};
    teamsData.forEach((teamData) => {
      const leagueId = teamData.league.id;
      if (!teamsByLeague[leagueId]) {
        teamsByLeague[leagueId] = {
          league: teamData.league,
          season: teamData.season,
          teams: []
        };
      }
      teamsByLeague[leagueId].teams.push(teamData);
    });

    const selectedTeamData = getSelectedTeamData();
    
    if (!selectedTeamData) {
      return null; // Shouldn't happen, but handle gracefully
    }

    const { team, league, season, leagueSeason, rank, currentWeek, currentPrediction, contestants } = selectedTeamData;

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Teams</h1>
            <p className="text-gray-600">You have teams in multiple leagues</p>
          </div>

          {/* Team Switcher */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Team
            </label>
            <select
              value={selectedTeamId || ''}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md focus:ring-survivor-500 focus:border-survivor-500"
            >
              {teamsData.map((teamData) => (
                <option key={teamData.team.id} value={teamData.team.id}>
                  {teamData.team.name} - {teamData.league.name} (Rank #{teamData.rank})
                </option>
              ))}
            </select>
          </div>

          {/* Selected Team Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 relative pb-16">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{team.name}</h2>
                <p className="text-gray-600 mt-1">{league.name}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Rank</div>
                <div className="text-3xl font-bold text-survivor-600">#{rank}</div>
              </div>
            </div>
            
            {/* Unclaim link in bottom right corner */}
            <div className="absolute bottom-4 right-6">
              <button
                onClick={() => handleUnclaimTeam(team.id, league.id, season.id)}
                disabled={unclaimingTeamId === team.id}
                className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {unclaimingTeamId === team.id ? 'Unclaiming...' : 'Unclaim team'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Total Score</div>
                <div className="text-2xl font-bold text-gray-900">{getTeamScore(team)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Current Week</div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeek ? `Week ${currentWeek.weekNumber}` : 'Closed'}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500 mb-1">Week Status</div>
                <div className="text-lg font-semibold">
                  {currentWeek ? (
                    <div>
                      <span className="text-green-600 flex items-center mb-1">
                        <CheckCircleIcon className="h-5 w-5 mr-1" />
                        Open for Predictions
                      </span>
                      {currentPrediction ? (
                        <div className="mt-2 text-sm font-normal text-gray-700">
                          <div className="text-xs text-gray-500 mb-1">Your Prediction:</div>
                          <div className="text-gray-900">
                            {getPredictionDisplay(currentPrediction, contestants) || 'Prediction submitted'}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-sm font-normal text-gray-500">
                          No prediction submitted yet
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500 flex items-center">
                      <XCircleIcon className="h-5 w-5 mr-1" />
                      No Open Weeks
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-wrap gap-3 items-center w-full">
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="start"
              />
              {currentWeek && (
                <Link
                  to={`/predictions2/${league.id}/${season.id}?teamId=${team.id}&week=${currentWeek.weekNumber}`}
                  className="px-6 py-3 bg-survivor-600 text-white rounded-md hover:bg-survivor-700 font-medium"
                >
                  {currentPrediction ? 'Update Prediction' : 'Submit Prediction'}
                </Link>
              )}
              <Link
                to={`/player/teams/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Team Detail
              </Link>
              <Link
                to={`/player/standings/${league.id}/${season.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View Standings
              </Link>
              <Link
                to={`/player/history/${team.id}`}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                View History
              </Link>
              <DraftHomeLink
                leagueId={league.id}
                seasonId={season.id}
                draftStatus={leagueSeason?.draftStatus}
                placement="end"
              />
            </div>
          </div>

          {/* League Sections with Team Tiles */}
          <div className="space-y-6">
            {Object.values(teamsByLeague).map((leagueData) => {
              const { league: leagueItem, season: seasonItem, teams } = leagueData;
              
              return (
                <div key={leagueItem.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">{leagueItem.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">Season {seasonItem.name || seasonItem.id}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map((teamData) => (
                      <div
                        key={teamData.team.id}
                        onClick={() => setSelectedTeamId(teamData.team.id)}
                        className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                          selectedTeamId === teamData.team.id
                            ? 'border-survivor-600 bg-survivor-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">
                            {teamData.team.name}
                          </h4>
                          <span className="text-sm font-medium text-survivor-600">#{teamData.rank}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          Score: {getTeamScore(teamData.team)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <PastSeasonsSection
            closedTeamsData={closedTeamsData}
            isOpen={pastSeasonsOpen}
            onToggle={() => setPastSeasonsOpen(!pastSeasonsOpen)}
          />
        </div>
      </div>
    );
  }

  return null;
}

